import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { ToolbarButton, ToolbarButtonGroup, ToolbarTooltip } from '@atlaskit/editor-toolbar';

type RemixDisabledReason = 'archived' | 'version-history';

const messages = defineMessages({
	remixInProgressTooltip: {
		id: 'highlight-actions.remix-action.in-progress-tooltip',
		defaultMessage: 'Remix in progress...',
		description:
			'Tooltip shown on the disabled Remix button when a remix session is already in progress',
	},
	remixToolbarTooltip: {
		id: 'highlight-actions.remix-action.toolbar-tooltip',
		defaultMessage: 'Remix',
		description:
			'Tooltip for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixToolbarLabel: {
		id: 'highlight-actions.remix-action.toolbar-label',
		defaultMessage: 'Remix',
		description:
			'Label for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixDisabledTooltipArchived: {
		id: 'highlight-actions.remix-action.disabled-tooltip-archived',
		defaultMessage: "Content on archived pages can't be remixed",
		description: 'Tooltip shown on the disabled Remix button when the page is archived',
	},
	remixDisabledTooltipVersionHistory: {
		id: 'highlight-actions.remix-action.disabled-tooltip-version-history',
		defaultMessage: 'Remix is unavailable in version history',
		description:
			'Tooltip shown on the disabled Remix button when viewing a previous version of the page',
	},
});

const getRemixDisabledTooltip = (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	reason?: RemixDisabledReason,
): string => {
	switch (reason) {
		case 'version-history':
			return formatMessage(messages.remixDisabledTooltipVersionHistory);
		case 'archived':
		default:
			return formatMessage(messages.remixDisabledTooltipArchived);
	}
};

export const RemixAction = ({
	isNewToolbarEnabled,
	onClick,
	isDisabled,
	isRemixInProgress,
	remixDisabledReason,
}: {
	isNewToolbarEnabled?: boolean;
	onClick?: () => void;
	isDisabled?: boolean;
	isRemixInProgress?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const tooltipContent = isRemixInProgress
		? formatMessage(messages.remixInProgressTooltip)
		: isDisabled
			? getRemixDisabledTooltip(formatMessage, remixDisabledReason)
			: formatMessage(messages.remixToolbarTooltip);

	return isNewToolbarEnabled ? (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButtonGroup>
				<ToolbarButton
					iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
					testId="highlight-actions-remix"
					onClick={onClick}
					isDisabled={isDisabled || isRemixInProgress}
				>
					<FormattedMessage {...messages.remixToolbarLabel} />
				</ToolbarButton>
			</ToolbarButtonGroup>
		</ToolbarTooltip>
	) : (
		<ToolbarTooltip content={tooltipContent}>
			<Button
				appearance="subtle"
				iconBefore={AiGenerativeRemixIcon}
				testId="highlight-actions-remix"
				onClick={onClick}
				isDisabled={isDisabled || isRemixInProgress}
			>
				<FormattedMessage {...messages.remixToolbarLabel} />
			</Button>
		</ToolbarTooltip>
	);
};
